import * as THREE from 'three';
import type { V2 } from '../../core/math';
import type { PropDecor, SignalHeadDecor } from '../decor';
import { PAVE_Y } from './ground';
import { ChunkedBuilders } from './meshBuilder';
import { emitProp, type StreetModelSet } from './props';
import { buildSignalStatics } from './signalsView';
import type { SignAtlas } from './textures';

/**
 * Knocked-over street furniture (DRIVING-3): lamp posts, signal poles and Belisha beacons are drawn
 * in the merged chunk geometry, so a knocked post is hidden there by a small fragment patch (a
 * vertical capsule per knocked item, up to MAX_KNOCKED) and replaced by its own toppling copy that
 * falls over in the direction of the hit and stays lying on the pavement / road. The copy is built
 * the way the standing one was: the Blender street model (the banded Belisha post, the lamp column
 * with its litter bin) when the street pack is in, else the procedural prop.
 */
export const MAX_KNOCKED = 64;

export type KnockKind = 'lamp' | 'signal' | 'belisha';

export interface KnockdownView {
  /** Patch a merged material so knocked items vanish from it. */
  patch(mat: THREE.Material): void;
  /** Topple one item. `fall` is the ground direction it falls towards (unit vector). */
  knock(kind: KnockKind, item: PropDecor | SignalHeadDecor, fall: V2): boolean;
  readonly count: number;
  update(dt: number): void;
  dispose(): void;
}

interface Faller {
  pivot: THREE.Group;
  axis: THREE.Vector3;
  t: number;
  done: boolean;
}

/** Capsule (ground segment + radius) covering what to hide for one item. */
function hideCapsule(kind: KnockKind, item: PropDecor | SignalHeadDecor): THREE.Vector4 {
  const p = item.pos;
  if (kind === 'lamp') {
    // Column at p, swan-neck arm + lantern out to 1.45 m along the heading.
    return new THREE.Vector4(p.x, p.z, p.x + Math.sin(item.heading) * 1.45, p.z + Math.cos(item.heading) * 1.45);
  }
  return new THREE.Vector4(p.x, p.z, p.x + Math.sin(item.heading) * 0.15, p.z + Math.cos(item.heading) * 0.15);
}
const HIDE_RADIUS: Record<KnockKind, number> = { lamp: 0.32, signal: 0.42, belisha: 0.24 };

/** The Belisha globe as cityView's `beacons` mesh draws it (its dull amber when the lamp is off). */
export const BELISHA_GLOBE = { y0: 2.52, y1: 2.86, r: 0.17 };
const GLOBE_OFF: [number, number, number] = [0.42, 0.21, 0.04];

export function createKnockdownView(scene: THREE.Scene, signs: SignAtlas, models: StreetModelSet | null = null): KnockdownView {
  const segs: THREE.Vector4[] = [];
  const radii: number[] = [];
  for (let i = 0; i < MAX_KNOCKED; i++) {
    segs.push(new THREE.Vector4(1e6, 1e6, 1e6, 1e6));
    radii.push(0);
  }
  const uniforms = { uKnockSeg: { value: segs }, uKnockR: { value: radii }, uKnockN: { value: 0 } };
  const fallMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.45, envMapIntensity: 0.8 });
  const fallers: Faller[] = [];
  const group = new THREE.Group();
  group.name = 'knocked-furniture';
  scene.add(group);

  return {
    get count() {
      return uniforms.uKnockN.value;
    },
    patch(mat) {
      const prev = mat.onBeforeCompile;
      const prevKey = mat.customProgramCacheKey.bind(mat);
      mat.onBeforeCompile = (shader, renderer) => {
        prev.call(mat, shader, renderer);
        Object.assign(shader.uniforms, uniforms);
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vKnockWorld;')
          .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvKnockWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;');
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', `#include <common>\nvarying vec3 vKnockWorld;\nuniform vec4 uKnockSeg[${MAX_KNOCKED}];\nuniform float uKnockR[${MAX_KNOCKED}];\nuniform int uKnockN;`)
          .replace(
            'void main() {',
            `void main() {
  for (int i = 0; i < ${MAX_KNOCKED}; i++) {
    if (i >= uKnockN) break;
    vec4 s = uKnockSeg[i];
    vec2 ab = s.zw - s.xy;
    vec2 ap = vKnockWorld.xz - s.xy;
    float h = clamp(dot(ap, ab) / max(dot(ab, ab), 1e-4), 0.0, 1.0);
    if (length(ap - ab * h) < uKnockR[i] && vKnockWorld.y > ${(PAVE_Y + 0.02).toFixed(2)}) discard;
  }`,
          );
      };
      mat.customProgramCacheKey = () => `${prevKey()}|knock`;
      mat.needsUpdate = true;
    },
    knock(kind, item, fall) {
      const n = uniforms.uKnockN.value;
      if (n >= MAX_KNOCKED) return false;
      segs[n]!.copy(hideCapsule(kind, item));
      radii[n] = HIDE_RADIUS[kind];
      uniforms.uKnockN.value = n + 1;

      // Standalone copy of the item, built around its base so it can rotate about it. With the street models
      // in, emitProp puts the procedural prop into the `…Low` builders as the LOW stand-in: those are left out,
      // or the copy would carry both. The Belisha takes its globe with it (dark: the flashing one is hidden
      // with the post by the capsule above).
      const cb = new ChunkedBuilders(1e6, -5e5);
      if (kind === 'signal') buildSignalStatics([item as SignalHeadDecor], cb);
      else emitProp(item as PropDecor, cb, signs, models);
      if (kind === 'belisha') {
        const g = BELISHA_GLOBE;
        cb.get('props', item.pos.x, item.pos.z).cylinder(item.pos.x, item.pos.z, PAVE_Y + g.y0, PAVE_Y + g.y1, g.r, g.r, 10, GLOBE_OFF, [0, 0, 1, 1]);
      }
      const geos: THREE.BufferGeometry[] = [];
      for (const [material, , b] of cb.entries()) {
        if (models && material.endsWith('Low')) continue;
        const g = b.build();
        if (g) geos.push(g);
      }
      const pivot = new THREE.Group();
      pivot.position.set(item.pos.x, PAVE_Y, item.pos.z);
      for (const g of geos) {
        g.translate(-item.pos.x, -PAVE_Y, -item.pos.z);
        const mesh = new THREE.Mesh(g, fallMat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        pivot.add(mesh);
      }
      group.add(pivot);
      // Rotate about the horizontal axis perpendicular to the fall direction: up x fall.
      const axis = new THREE.Vector3(fall.z, 0, -fall.x).normalize();
      fallers.push({ pivot, axis, t: 0, done: false });
      return true;
    },
    update(dt) {
      for (const f of fallers) {
        if (f.done) continue;
        f.t += dt;
        // Gravity-like ease-in to ~86 degrees (it rests on its base collar / lantern).
        const k = Math.min(1, (f.t / 0.75) ** 2);
        f.pivot.quaternion.setFromAxisAngle(f.axis, k * 1.5);
        if (k >= 1) f.done = true;
      }
    },
    dispose() {
      scene.remove(group);
      for (const f of fallers) for (const c of f.pivot.children) (c as THREE.Mesh).geometry.dispose();
      fallMat.dispose();
    },
  };
}
